import { useEffect, useRef } from 'react';
import { isEqual } from 'lodash-es';
import { type AnySchema } from 'yup';
import {
    SwarmType,
    ReportTemplatePageMarginUnit,
    ReportTemplatePageOrientation,
    ReportTemplatePageSize,
} from '@/graphql/types';
import { useDeepCompareMemoize } from '../../../../hooks/useDeepCompareMemoize';
import { isRecord } from '../../../../utils/typeGuards';
import type { FieldValues } from '../../../reactForm/types';
import { LayoutTree } from '../../../reactForm/layout-helper';
import {
    buildStrictContractYupSchemaFromFormSchema,
    type StrictContractFormSchemaField,
} from '../../../reactForm/validation';
import { FieldsArray } from '../../measuringPoint/fields-array';
import type { SwarmFormControllerConfig } from '../../measuringPoint/form-controller';
import { SwarmFormController } from '../../measuringPoint/form-controller';
import type { TranslatedFormSchema } from '../../measuringPoint/types';
import type { ElementDefinition } from '../elements/types';
import {
    REPORT_TEMPLATE_PAGE_MARGIN_UNIT_TRANSLATIONS,
    REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS,
    REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS,
} from '../enums';
import {
    DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN,
    buildPageMarginValueValidationSchema,
    parsePageMargin,
} from '../pageSettings';
import {
    isDefinitionField,
    isReportTemplatePageOrientation,
    isReportTemplatePageSize,
} from '../typeGuards';
import { parseDefinitions } from '../elements/renderHelpers';
import { toErrorMessage } from './errorMessage';
import { useStableCallback } from './useStableCallback';

const STRUCTURE_FIELD = 'structure';
export const REPORT_TEMPLATE_NAME_MAX_LENGTH = 100;
const PAGE_MARGIN_UNIT_FIELD = 'pageMarginUnit';

interface ControllerConfig extends SwarmFormControllerConfig {
    template: ReportTemplateSettingsValues;
    onSubmitTemplate: (
        nextSettings: ReportTemplateSettingsSubmitPayload
    ) => Promise<ReportTemplateSettingsValues>;
}

interface UseReportTemplateSettingsFormControllerArgs {
    template: ReportTemplateSettingsValues;
    onSubmitTemplate: (
        nextSettings: ReportTemplateSettingsSubmitPayload
    ) => Promise<ReportTemplateSettingsValues>;
}

export interface ReportTemplateSettingsSubmitPayload {
    name: string;
    structure: ElementDefinition[];
    pageSize: ReportTemplatePageSize;
    pageOrientation: ReportTemplatePageOrientation;
    // The margin is one `{value, unit}` contract on the API; the form edits it as two controls.
    pageMarginValue: number;
    pageMarginUnit: ReportTemplatePageMarginUnit;
}

export interface ReportTemplateSettingsValues extends ReportTemplateSettingsSubmitPayload {
    id: string;
}

interface TemplateSettingsFieldSpec extends StrictContractFormSchemaField {
    readonly name: keyof ReportTemplateSettingsSubmitPayload;
    readonly type: 'char' | 'choice' | 'float' | 'definitions';
    readonly default: unknown;
    readonly buildValidationSchema?: (baseSchema: AnySchema) => AnySchema;
    readonly getLabel?: () => string;
}

function buildStructureValidationSchema(baseSchema: AnySchema) {
    return baseSchema.test(
        'valid-structure',
        'INVALID_REPORT_TEMPLATE_STRUCTURE',
        function test(value) {
            if (typeof value === 'undefined' || value === null) {
                return true;
            }

            try {
                parseDefinitions(value, [STRUCTURE_FIELD]);
                return true;
            } catch (error) {
                return this.createError({
                    message: toErrorMessage(error),
                });
            }
        }
    );
}

export const TEMPLATE_SETTINGS_FIELD_SPECS: readonly TemplateSettingsFieldSpec[] = [
    {
        name: 'name',
        type: 'char',
        required: true,
        maxLength: REPORT_TEMPLATE_NAME_MAX_LENGTH,
        default: '',
        getLabel: () => gettext('NAME'),
    },
    {
        name: 'pageSize',
        type: 'choice',
        required: true,
        validateOptions: true,
        get options() {
            return Object.values(ReportTemplatePageSize).map(
                (value): [ReportTemplatePageSize, string] => [
                    value,
                    REPORT_TEMPLATE_PAGE_SIZE_TRANSLATIONS[value],
                ]
            );
        },
        default: ReportTemplatePageSize.A4,
        getLabel: () => gettext('PAGE_SIZE'),
    },
    {
        name: 'pageOrientation',
        type: 'choice',
        required: true,
        validateOptions: true,
        get options() {
            return Object.values(ReportTemplatePageOrientation).map(
                (value): [ReportTemplatePageOrientation, string] => [
                    value,
                    REPORT_TEMPLATE_PAGE_ORIENTATION_TRANSLATIONS[value],
                ]
            );
        },
        default: ReportTemplatePageOrientation.Portrait,
        getLabel: () => gettext('ORIENTATION'),
    },
    {
        name: 'pageMarginValue',
        type: 'float',
        required: true,
        default: DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN.value,
        buildValidationSchema: buildPageMarginValueValidationSchema(PAGE_MARGIN_UNIT_FIELD),
        getLabel: () => gettext('MARGIN'),
    },
    {
        name: PAGE_MARGIN_UNIT_FIELD,
        type: 'choice',
        required: true,
        validateOptions: true,
        get options() {
            return Object.values(ReportTemplatePageMarginUnit).map(
                (value): [ReportTemplatePageMarginUnit, string] => [
                    value,
                    REPORT_TEMPLATE_PAGE_MARGIN_UNIT_TRANSLATIONS[value],
                ]
            );
        },
        default: DEFAULT_REPORT_TEMPLATE_PAGE_MARGIN.unit,
        getLabel: () => gettext('MARGIN_UNIT'),
    },
    {
        name: STRUCTURE_FIELD,
        type: 'definitions',
        required: true,
        default: [],
        buildValidationSchema: buildStructureValidationSchema,
    },
];

function assertRecord(value: unknown, valuePath: string): Record<string, unknown> {
    if (!isRecord(value)) {
        throw new Error(`Expected "${valuePath}" to be an object.`);
    }

    return value;
}

function assertPageSetting<TValue>(
    isSupported: (value: unknown) => value is TValue,
    value: unknown,
    fieldName: string
): TValue {
    if (!isSupported(value)) {
        throw new Error(`Unsupported "template.${fieldName}" value "${String(value)}".`);
    }

    return value;
}

function normalizeTemplateSettingsValues(value: unknown): ReportTemplateSettingsValues {
    const record = assertRecord(value, 'template');
    const { id } = record;
    if (typeof id !== 'string' || !id.trim()) {
        throw new Error('Expected "template.id" to be a non-empty string.');
    }

    // The margin is validated as the pair it is persisted as, so the two form controls cannot
    // start out holding a combination the API would reject.
    const pageMargin = parsePageMargin(
        { value: record.pageMarginValue, unit: record.pageMarginUnit },
        'template.pageMargin'
    );

    return {
        id,
        structure: parseDefinitions(record.structure, ['template.structure']),
        name: typeof record.name === 'string' ? record.name : '',
        pageSize: assertPageSetting(isReportTemplatePageSize, record.pageSize, 'pageSize'),
        pageOrientation: assertPageSetting(
            isReportTemplatePageOrientation,
            record.pageOrientation,
            'pageOrientation'
        ),
        pageMarginValue: pageMargin.value,
        pageMarginUnit: pageMargin.unit,
    };
}

export class ReportTemplateSettingsFormController extends SwarmFormController {
    declare template: ReportTemplateSettingsValues;

    declare onSubmitTemplate: (
        nextSettings: ReportTemplateSettingsSubmitPayload
    ) => Promise<ReportTemplateSettingsValues>;

    constructor(config: ControllerConfig) {
        super(config as SwarmFormControllerConfig);
    }

    storeConfig({ template, onSubmitTemplate, ...config }: ControllerConfig) {
        this.template = normalizeTemplateSettingsValues(template);
        this.onSubmitTemplate = onSubmitTemplate;

        super.storeConfig(config);
    }

    createValidationSchema() {
        return buildStrictContractYupSchemaFromFormSchema({
            fields: TEMPLATE_SETTINGS_FIELD_SPECS,
        });
    }

    createFields(): TranslatedFormSchema['fields'] {
        const fields = FieldsArray.from(
            TEMPLATE_SETTINGS_FIELD_SPECS.map((fieldSpec) => ({
                name: fieldSpec.name,
                label: fieldSpec.getLabel?.(),
                type: fieldSpec.type,
                required: fieldSpec.required,
                maxLength: fieldSpec.maxLength,
                validateOptions: fieldSpec.validateOptions,
                options: fieldSpec.options ? [...fieldSpec.options] : undefined,
                buildValidationSchema: fieldSpec.buildValidationSchema,
                default: this.template[fieldSpec.name],
            }))
        );

        // `TranslatedFormSchema['fields']` is still tied to measuring-point schema unions and
        // does not model report-builder-specific `definitions` fields yet.
        return Object.freeze(fields) as unknown as TranslatedFormSchema['fields'];
    }

    createLayoutTree(): LayoutTree {
        return new LayoutTree({
            name: 'root',
            groups: [
                new LayoutTree({
                    name: 'templateSettings',
                    fields: TEMPLATE_SETTINGS_FIELD_SPECS.filter(
                        (fieldSpec) => fieldSpec.name !== 'name' && !isDefinitionField(fieldSpec)
                    ).map((fieldSpec) => this.fields.getByName(fieldSpec.name)),
                }),
            ],
        });
    }

    createDefaultValues(): Record<string, unknown> {
        return Object.fromEntries(
            TEMPLATE_SETTINGS_FIELD_SPECS.map((fieldSpec) => [
                fieldSpec.name,
                this.template[fieldSpec.name],
            ])
        );
    }

    async setTemplate(nextTemplate: unknown): Promise<void> {
        const normalizedTemplate = normalizeTemplateSettingsValues(nextTemplate);

        if (isEqual(this.template, normalizedTemplate)) {
            return;
        }

        this.template = normalizedTemplate;
        await this.setValues(normalizedTemplate);
    }

    async onSubmitFunction(values: FieldValues): Promise<void> {
        this.setSaveIsInProgress(true);

        try {
            const submitPayload = values as ReportTemplateSettingsSubmitPayload;
            const updatedTemplate = await this.onSubmitTemplate(submitPayload);
            await this.setTemplate(updatedTemplate);
            this.onSubmitSuccessful$.next(updatedTemplate);
        } catch (error) {
            this.setGlobalError('templateSettings', toErrorMessage(error));
        } finally {
            this.setSaveIsInProgress(false);
        }
    }
}

export function useReportTemplateSettingsFormController({
    template,
    onSubmitTemplate,
}: UseReportTemplateSettingsFormControllerArgs): ReportTemplateSettingsFormController {
    const stableTemplate = useDeepCompareMemoize(normalizeTemplateSettingsValues(template));
    const stableOnSubmitTemplate = useStableCallback(onSubmitTemplate);
    const initialTemplateRef = useRef<ReportTemplateSettingsValues>(stableTemplate);

    const controller = ReportTemplateSettingsFormController.use(SwarmType.Vibration, false, {
        template: initialTemplateRef.current,
        onSubmitTemplate: stableOnSubmitTemplate,
    }) as ReportTemplateSettingsFormController;

    useEffect(() => {
        void controller.setTemplate(stableTemplate);
    }, [controller, stableTemplate]);

    return controller;
}
